import React, { useEffect, useState } from 'react';
import { Select, Form, InputNumber, type FormInstance, Divider, Spin, Tag } from 'antd';
import { Space, Text, Button, DatePicker } from '@g2u/ui-kit';
import { parse, parseISO } from 'date-fns';
import { useMutation } from 'react-query';
import { Big } from 'big.js';
import cx from 'classnames';

import { JobServiceTypeLabel, JsonDataItem, type JsonData, type WithOptionalDataTest } from 'types';
import { parseErrors, Toast, useJob } from 'helpers';
import { getProducts } from 'queries';
import { AddServiceModal } from '..';
import styles from './service-list.module.scss';
import { calculateTotalHours } from 'helpers/hooks/useTotalHours';

type SelectedProduct = {
  qty: string;
  details: {
    product: {
      labour_qty: string;
    };
  };
};

export namespace ServiceList {
  export type Props = {
    customerId: number;
    serviceId: number;
    job: JsonDataItem | null;
    form: FormInstance;
    isRequired?: boolean;
    totalPrice: string;
    onSelect: (products: JsonData | undefined) => void;
    onLoad: () => void;
  } & WithOptionalDataTest;

  export const $: React.FC<Props> = (props) => {
    const { job, onSelect, isRequired, form, totalPrice, onLoad, customerId, serviceId } = props;
    const [products, setProducts] = useState<JsonData>();
    const [selectedProducts, setSelectedProducts] = useState<JsonData | undefined>();
    const [showServiceModal, setShowServiceModal] = useState<string>();
    const { serviceSelection } = useJob(job?.ID);
    const isClosed = ['cancelled', 'completed'].includes(job?.status);
    const requestedAppointment = job?.attributes.find(
      (item: JsonDataItem) => item.key === 'requested_appointment',
    )?.value;

    const totalHours = calculateTotalHours(selectedProducts as SelectedProduct[]);

    const { mutate: getProductsList, isLoading: isProductsLoading } = useMutation(getProducts, {
      onSuccess: (data: JsonDataItem) => {
        setProducts(
          data.map((product: JsonDataItem) => {
            return {
              label: product.name,
              value: product?.ID?.toString(),
              details: product,
            };
          }),
        );
      },
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    });

    const handleAddProduct = (product: JsonDataItem) => {
      setSelectedProducts(
        selectedProducts ? [...selectedProducts.filter((item) => item.id !== product.id), product] : [product],
      );
      setShowServiceModal(undefined);
    };

    const handleRemoveProduct = (productId: number) => {
      const updatedProducts = selectedProducts?.filter((product) => product.id !== productId);
      setSelectedProducts(updatedProducts?.length ? updatedProducts : undefined);
    };

    useEffect(() => {
      onLoad();
      getProductsList({ customerId, serviceId });
    }, []);

    useEffect(() => {
      onSelect(
        selectedProducts?.map((product) => {
          const date = requestedAppointment || product.details.service_technicians[0]?.appointment;
          date && form.setFieldValue(`productDate-${product.id}`, parseISO(date));
          return { ...product, date };
        }),
      );
    }, [selectedProducts]);

    useEffect(() => {
      const productSelection = job?.products.map((item: JsonDataItem) => {
        return { id: item.product?.ID.toString(), qty: item.product.quantity.toString(), details: item };
      });
      setSelectedProducts(productSelection);
    }, [job]);

    return (
      <Space.$ direction={Space.Direction.Vertical} className={styles.serviceList}>
        <Text.$ strong size={Text.Size.LG} className={styles.serviceList__title}>
          Services and Fees
        </Text.$>
        {isProductsLoading ? (
          <Space.$ align={Space.Align.Center} direction={Space.Direction.Vertical}>
            <Spin />
          </Space.$>
        ) : (
          <>
            <Tag color="blue">
              {
                JobServiceTypeLabel[
                  serviceSelection?.service_attributes?.job_service_type as keyof typeof JobServiceTypeLabel
                ]
              }
            </Tag>
            <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small} fullWidth>
              <Form.Item name="products" required rules={isClosed ? [] : [{ required: true }]}>
                <div>
                  {selectedProducts?.map((selectedProduct, index) => (
                    <div className={styles.serviceList__item} key={index}>
                      <Select
                        disabled
                        value={{ value: selectedProduct.id, label: selectedProduct.details.product.name }}
                        style={{ width: '100%', marginRight: 8 }}
                        placeholder="Select a product"
                        optionFilterProp="children"
                        className={cx(styles.serviceList__select, isRequired && styles.serviceList__select_required)}
                        showSearch
                      >
                        {products
                          ?.filter(
                            (product) =>
                              !selectedProducts
                                .filter((item) => item.id !== selectedProduct.id) // exclude the current selection
                                .map((selected) => selected.id) // get remaining selected product ID's
                                .includes(product.value), // remove from available products already selected
                          )
                          .map((option) => (
                            <Select.Option key={option.value} value={option.value}>
                              {option.label}
                            </Select.Option>
                          ))}
                      </Select>
                      {selectedProduct?.id && (
                        <>
                          <InputNumber
                            disabled
                            value={selectedProduct.qty}
                            min={1}
                            width={55}
                            className={cx(styles.serviceList__qty, isRequired && styles.serviceList__qty_required)}
                          />
                          <DatePicker.$
                            disabled
                            initialValue={
                              selectedProduct.details.service_technicians?.[0]?.appointment
                                ? (parse(
                                    selectedProduct.details.service_technicians[0]?.appointment,
                                    'yyyy-MM-dd HH:mm:ss',
                                    new Date(),
                                  ) as any)
                                : new Date()
                            }
                            name={`productDate-${selectedProduct.id}`}
                            allowClear={false}
                            className={styles.serviceList__datepicker}
                          />
                          <div className={styles.serviceList__price}>
                            <Text.$ strong size={Text.Size.SM} type="secondary">
                              {`${calculateTotalHours([selectedProduct as SelectedProduct])} hours`}
                            </Text.$>

                            {selectedProduct.id && (
                              <Text.$>
                                {' $'}
                                {Big(selectedProduct?.details?.product.total || '0')
                                  .times(selectedProduct.qty || '0')
                                  .toFixed(2)}
                                <small> inc GST</small>
                              </Text.$>
                            )}
                            <Space.$ justify={Space.Justify.End}>
                              {selectedProduct.details.product.duration && (
                                <Button.$
                                  dataTestId="edit-product-1"
                                  size={Button.Size.Small}
                                  type={Button.Type.Link}
                                  className={styles.serviceList__link}
                                  onClick={() => {
                                    setShowServiceModal(selectedProduct.id);
                                  }}
                                >
                                  Edit
                                </Button.$>
                              )}
                              <Button.$
                                dataTestId="remove-product-1"
                                size={Button.Size.Small}
                                type={Button.Type.Link}
                                className={styles.serviceList__link}
                                onClick={() => {
                                  handleRemoveProduct(selectedProduct.id);
                                }}
                              >
                                Remove
                              </Button.$>
                            </Space.$>
                          </div>
                        </>
                      )}
                    </div>
                  ))}

                  <Button.$
                    type={Button.Type.Dashed}
                    block
                    dataTestId="add-new-product"
                    className={styles.serviceList__action}
                    onClick={() => setShowServiceModal('0')}
                    style={{ marginTop: 8 }}
                  >
                    + Add New
                  </Button.$>
                </div>
              </Form.Item>
            </Space.$>

            {selectedProducts && selectedProducts.filter((prod) => prod.id).length > 0 && (
              <>
                <Divider />
                <div className={styles.serviceList__total}>
                  <Text.$ size={Text.Size.SM}>
                    Total <br />
                    <b>{selectedProducts.filter((prod) => prod.id).length}</b> items
                  </Text.$>
                  <div className={styles.serviceList__totalHours}>
                    <Text.$ strong size={Text.Size.MD} type="secondary">
                      {`${totalHours} hours`}
                    </Text.$>
                    <Text.$ strong>
                      ${totalPrice} <small>inc GST</small>
                    </Text.$>
                  </div>
                </div>
              </>
            )}

            <AddServiceModal.$
              onOk={(product: JsonDataItem) => handleAddProduct(product)}
              onCancel={() => setShowServiceModal(undefined)}
              isOpen={!!showServiceModal}
              selectedProducts={selectedProducts}
              currentProductId={showServiceModal}
              productList={products}
              isGeekcoverMember={job?.customer.is_geek_cover}
            />
          </>
        )}
      </Space.$>
    );
  };
}
